import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchMembers, gymCheckin, fetchGymAttendance, fetchServices, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";

// Gym / spa front-desk check-in. Two paths: a member checks in (free, covered
// by their dues — shows pay status + a renew prompt if overdue) or a walk-in
// buys a drop-in day pass (rung through the till so no cash goes unrecorded).

const METHODS = ["cash", "telebirr", "cbe_birr", "pos", "other"];

const S = {
  en: {
    title: "Check-in", sub: "Check members in, or sell a drop-in day pass.",
    tab_member: "Member", tab_drop: "Drop-in pass",
    search: "Search member by name or phone…", none: "No members match.",
    check_in: "Check in", checked: "Checked in", overdue: "Overdue — renew", due_soon: "Due soon", current: "Active",
    renew: "Renew", name_opt: "Name (optional)", amount: "Amount", method: "Method", sell: "Check in & charge",
    today: "Today", members: "Members", dropins: "Drop-ins", drop_rev: "Drop-in revenue",
    at: "at", walkin: "Walk-in", ok_member: "{n} checked in", ok_drop: "Day pass sold to {n}",
    enter_amount: "Enter a drop-in amount.",
    pass: "Day pass", choose_pass: "Choose a pass…", custom: "Other / custom amount",
    no_passes: "No day passes set up yet.", setup_passes: "Set up passes", pick_pass: "Choose a day pass.",
  },
  am: {
    title: "መግቢያ", sub: "አባላትን ያስገቡ ወይም የቀን መግቢያ ይሽጡ።",
    tab_member: "አባል", tab_drop: "የቀን መግቢያ",
    search: "አባል በስም ወይም ስልክ ይፈልጉ…", none: "የሚዛመድ አባል የለም።",
    check_in: "አስገባ", checked: "ገብቷል", overdue: "ጊዜው አልፏል — አድስ", due_soon: "በቅርቡ የሚያልቅ", current: "ንቁ",
    renew: "አድስ", name_opt: "ስም (አማራጭ)", amount: "መጠን", method: "ዘዴ", sell: "አስገባ እና አስከፍል",
    today: "ዛሬ", members: "አባላት", dropins: "የቀን መግቢያዎች", drop_rev: "የቀን መግቢያ ገቢ",
    at: "በ", walkin: "ጎብኚ", ok_member: "{n} ገብቷል", ok_drop: "የቀን መግቢያ ለ{n} ተሽጧል",
    enter_amount: "የመግቢያ መጠን ያስገቡ።",
    pass: "የቀን መግቢያ", choose_pass: "መግቢያ ይምረጡ…", custom: "ሌላ / ልዩ መጠን",
    no_passes: "እስካሁን የቀን መግቢያ አልተዘጋጀም።", setup_passes: "መግቢያዎችን ያዘጋጁ", pick_pass: "የቀን መግቢያ ይምረጡ።",
  },
  om: {
    title: "Galmee", sub: "Miseensota galchi, yookiin galmee guyyaa gurgurii.",
    tab_member: "Miseensa", tab_drop: "Galmee guyyaa",
    search: "Miseensa maqaa/bilbilaan barbaadi…", none: "Miseensi wal fakkaatu hin jiru.",
    check_in: "Galchi", checked: "Galmaa'e", overdue: "Yeroon darbe — haaromsi", due_soon: "Dhiyootti", current: "Sochii",
    renew: "Haaromsi", name_opt: "Maqaa (filannoo)", amount: "Hamma", method: "Mala", sell: "Galchi & kaffalchiisi",
    today: "Har'a", members: "Miseensota", dropins: "Galmee guyyaa", drop_rev: "Galii galmee guyyaa",
    at: "sa'a", walkin: "Keessummaa", ok_member: "{n} galmaa'e", ok_drop: "Galmeen guyyaa {n}f gurgurame",
    enter_amount: "Hamma galmee guyyaa galchi.",
    pass: "Galmee guyyaa", choose_pass: "Galmee filadhu…", custom: "Kan biraa / hamma addaa",
    no_passes: "Galmeen guyyaa hin qophoofne.", setup_passes: "Galmee qopheessi", pick_pass: "Galmee guyyaa filadhu.",
  },
};

export default function CheckInPage() {
  const lang = (typeof window !== "undefined" && localStorage.getItem("lang")) || "en";
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (n) => formatPrice(Number(n || 0), cur);

  const [mode, setMode] = useState("member");
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [att, setAtt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);      // {ok, text}
  const [dropName, setDropName] = useState("");
  const [dropAmt, setDropAmt] = useState("");
  const [dropMethod, setDropMethod] = useState("cash");
  // Day-pass products = access-type services (no fixed duration) with a set price.
  const [passes, setPasses] = useState([]);
  const [passId, setPassId] = useState("");   // "" = none yet, "custom" = free amount

  const loadAtt = useCallback(async () => {
    try { setAtt(await fetchGymAttendance()); } catch { /* keep prior */ }
  }, []);
  useEffect(() => { fetchMembers().then(setMembers).catch(() => {}); loadAtt(); }, [loadAtt]);
  useEffect(() => {
    fetchServices()
      .then((r) => setPasses((r || []).filter((s) => s.active !== false && (s.duration_min == null))))
      .catch(() => setPasses([]));
  }, []);
  const selectedPass = passes.find((p) => String(p.id) === String(passId));

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return members.slice(0, 8);
    return members.filter((m) =>
      (m.name || "").toLowerCase().includes(s) || (m.phone || "").includes(s)).slice(0, 12);
  }, [members, q]);

  const payChip = (state) => {
    const map = { overdue: ["#b02a2a", "#fdecec", t("overdue")], due_soon: ["#b8860b", "#fff8e7", t("due_soon")], current: ["#2b8a3e", "#f2fbf4", t("current")], frozen: ["#667085", "#eef0f3", "frozen"] };
    const [c, bg, label] = map[state] || map.current;
    return <span style={{ color: c, background: bg, borderRadius: 12, padding: "1px 8px", fontSize: 12, fontWeight: 700 }}>{label}</span>;
  };

  const checkMember = async (m) => {
    setBusy(true); setMsg(null);
    try {
      const res = await gymCheckin({ membership_id: m.id });
      setMsg({ ok: true, text: t("ok_member").replace("{n}", res.name || m.name), member: res.member });
      await loadAtt();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || "Could not check in." }); }
    finally { setBusy(false); }
  };

  const sellDropIn = async () => {
    const usingPass = selectedPass && passId !== "custom";
    // A pass sets its own price server-side; custom keeps the free-form amount.
    if (passes.length > 0 && !usingPass && passId !== "custom") { setMsg({ ok: false, text: t("pick_pass") }); return; }
    if (!usingPass && !(Number(dropAmt) > 0)) { setMsg({ ok: false, text: t("enter_amount") }); return; }
    setBusy(true); setMsg(null);
    try {
      const body = usingPass
        ? { drop_in: true, name: dropName.trim() || null, service_id: selectedPass.id, method: dropMethod }
        : { drop_in: true, name: dropName.trim() || null, amount: Number(dropAmt), method: dropMethod };
      const res = await gymCheckin(body);
      setMsg({ ok: true, text: t("ok_drop").replace("{n}", res.name || t("walkin")) });
      setDropName(""); setDropAmt(""); setPassId(passes.length > 0 ? "" : "");
      await loadAtt();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || "Could not sell the pass." }); }
    finally { setBusy(false); }
  };

  const tabBtn = (key, label) => (
    <button onClick={() => { setMode(key); setMsg(null); }}
      style={{ padding: "8px 16px", borderRadius: 20, border: "1px solid " + (mode === key ? "#1f6f4a" : "#d3d8de"),
               background: mode === key ? "#1f6f4a" : "#fff", color: mode === key ? "#fff" : "#334", fontWeight: 600, cursor: "pointer" }}>
      {label}
    </button>
  );

  return (
    <div className="page">
      <h1 style={{ marginBottom: 2 }}>🎫 {t("title")}</h1>
      <p className="muted" style={{ marginTop: 0 }}>{t("sub")}</p>

      <div style={{ display: "flex", gap: 8, margin: "10px 0 14px" }}>
        {tabBtn("member", t("tab_member"))}
        {tabBtn("drop", t("tab_drop"))}
      </div>

      {msg && (
        <div style={{ background: msg.ok ? "#f2fbf4" : "#fdecec", color: msg.ok ? "#2b8a3e" : "#b02a2a",
                      border: "1px solid " + (msg.ok ? "#cdeed5" : "#f3c2c2"), padding: "8px 12px", borderRadius: 8, marginBottom: 12,
                      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <span>{msg.ok ? "✓ " : "⚠ "}{msg.text}</span>
          {msg.member && msg.member.pay_state === "overdue" &&
            <Link className="btn" to="/members" style={{ padding: "3px 10px" }}>{t("renew")}</Link>}
        </div>
      )}

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* left: the action */}
        <div style={{ flex: "1 1 340px", background: "#fff", border: "1px solid #eef0f3", borderRadius: 12, padding: 16 }}>
          {mode === "member" ? (
            <>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} autoFocus
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #d3d8de", borderRadius: 8, marginBottom: 8 }} />
              {filtered.length === 0 && <div className="muted">{t("none")}</div>}
              {filtered.map((m) => (
                <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: "1px solid #f2f4f6" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{m.name || "—"} {payChip(m.pay_state)}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{m.plan_name}{m.phone ? ` · ${m.phone}` : ""}</div>
                  </div>
                  <button className="btn btn-primary" disabled={busy} onClick={() => checkMember(m)} style={{ padding: "6px 12px" }}>{t("check_in")}</button>
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="field"><label>{t("name_opt")}</label>
                <input value={dropName} onChange={(e) => setDropName(e.target.value)} placeholder={t("walkin")} style={{ width: "100%", padding: 10 }} /></div>
              {passes.length > 0 ? (
                <div className="field" style={{ marginTop: 10 }}><label>{t("pass")}</label>
                  <select value={passId} onChange={(e) => setPassId(e.target.value)} style={{ width: "100%", padding: 10 }}>
                    <option value="">{t("choose_pass")}</option>
                    {passes.map((p) => <option key={p.id} value={p.id}>{p.name} — {money(p.price_public)}</option>)}
                    <option value="custom">{t("custom")}</option>
                  </select></div>
              ) : (
                <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>
                  {t("no_passes")} <Link to="/services">{t("setup_passes")}</Link>
                </div>
              )}
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                {(passes.length === 0 || passId === "custom") && (
                  <div className="field" style={{ flex: 1 }}><label>{t("amount")}</label>
                    <input type="number" min="0" value={dropAmt} onChange={(e) => setDropAmt(e.target.value)} style={{ width: "100%", padding: 10 }} /></div>
                )}
                <div className="field" style={{ flex: 1 }}><label>{t("method")}</label>
                  <select value={dropMethod} onChange={(e) => setDropMethod(e.target.value)} style={{ width: "100%", padding: 10 }}>
                    {METHODS.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
                  </select></div>
              </div>
              <button className="btn btn-primary" disabled={busy} onClick={sellDropIn} style={{ marginTop: 12 }}>
                {t("sell")}{selectedPass && passId !== "custom" ? ` · ${money(selectedPass.price_public)}` : ""}
              </button>
            </>
          )}
        </div>

        {/* right: today's attendance */}
        <div style={{ flex: "1 1 300px", background: "#fff", border: "1px solid #eef0f3", borderRadius: 12, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>{t("today")}</h3>
          {att && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <span className="muted" style={{ fontSize: 13 }}>{t("members")}: <b>{att.members}</b></span>
              <span className="muted" style={{ fontSize: 13 }}>· {t("dropins")}: <b>{att.drop_ins}</b></span>
              <span className="muted" style={{ fontSize: 13 }}>· {t("drop_rev")}: <b>{money(att.drop_in_revenue)}</b></span>
            </div>
          )}
          {att && att.visits.length === 0 && <div className="muted">—</div>}
          {att && att.visits.map((v) => (
            <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid #f2f4f6" }}>
              <span style={{ fontVariantNumeric: "tabular-nums", minWidth: 44, fontWeight: 700 }}>{v.time}</span>
              <div style={{ flex: 1 }}>
                {v.name || t("walkin")}
                {v.type === "drop_in" && v.pass_name && <span className="muted" style={{ fontSize: 12 }}> · {v.pass_name}</span>}
              </div>
              <span className="muted" style={{ fontSize: 12 }}>{v.type === "drop_in" ? money(v.amount) : t("tab_member")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
